/**
 * Privacy Orchestrator — 统一隐私合规编排器
 * 
 * 跨所有 SDK 的一站式隐私操作：
 * - 一键数据删除 (GDPR 第17条: 被遗忘权)
 * - 一键数据导出 (GDPR 第20条: 数据可携带性)
 * - 统一同意管理
 * - 全平台数据声明
 * 
 * 使用场景:
 *   import { privacyOrchestrator } from '@quantabit/sdk-config';
 *   await privacyOrchestrator.deleteAllUserData(); // GDPR 数据删除
 *   const exportData = await privacyOrchestrator.exportAllUserData(); // 数据导出
 */

import { consentManager } from './ConsentManager';

/**
 * 隐私编排器
 */
class PrivacyOrchestratorClass {
  constructor() {
    // 注册的 SDK 清理器
    this._cleaners = new Map();
    // 注册的 SDK 数据导出器
    this._exporters = new Map();
    // 注册的 SDK 数据声明
    this._disclosures = new Map();
  }

  /**
   * 注册 SDK 的隐私操作
   * 各 SDK 在初始化时调用此方法注册自己的隐私处理函数
   * 
   * @param {string} sdkName - SDK 名称
   * @param {object} handlers - 隐私处理器
   * @param {function} handlers.clean - 数据清理函数 (应返回 boolean)
   * @param {function} handlers.export - 数据导出函数 (应返回 object)
   * @param {function} handlers.getDisclosure - 数据声明函数
   */
  register(sdkName, handlers = {}) {
    if (handlers.clean) this._cleaners.set(sdkName, handlers.clean);
    if (handlers.export) this._exporters.set(sdkName, handlers.export);
    if (handlers.getDisclosure) this._disclosures.set(sdkName, handlers.getDisclosure);
  }

  /**
   * 注销 SDK
   */
  unregister(sdkName) {
    this._cleaners.delete(sdkName);
    this._exporters.delete(sdkName);
    this._disclosures.delete(sdkName);
  }

  /**
   * 一键删除所有用户数据 — GDPR 第17条: 被遗忘权
   * 调用所有已注册 SDK 的数据清理函数
   * 
   * @returns {Promise<object>} 每个 SDK 的清理结果
   */
  async deleteAllUserData() {
    const results = {};
    const errors = [];

    for (const [sdkName, cleanFn] of this._cleaners) {
      try {
        const result = await cleanFn();
        results[sdkName] = { success: true, result };
      } catch (error) {
        results[sdkName] = { success: false, error: error.message };
        errors.push({ sdk: sdkName, error: error.message });
      }
    }

    // 清除通用 localStorage 数据
    this._clearGenericLocalStorage();

    // 撤销所有同意
    consentManager.init();
    consentManager.acceptEssentialOnly();

    return {
      timestamp: new Date().toISOString(),
      action: 'delete_all_user_data',
      gdprArticle: 'Art. 17 - Right to erasure',
      sdkResults: results,
      errors,
      localStorageCleared: true,
      consentReset: true,
    };
  }

  /**
   * 一键导出所有用户数据 — GDPR 第20条: 数据可携带性
   * 调用所有已注册 SDK 的数据导出函数
   * 
   * @param {object} options - 导出选项
   * @param {string} options.format - 导出格式 (json/csv)
   * @returns {Promise<object>} 合并的导出数据
   */
  async exportAllUserData(options = {}) {
    const exportData = {
      exportDate: new Date().toISOString(),
      format: 'QBit Platform Data Export (GDPR Art. 20)',
      platform: 'QuantaBit',
      sdkData: {},
      errors: [],
    };

    for (const [sdkName, exportFn] of this._exporters) {
      try {
        const data = await exportFn(options);
        exportData.sdkData[sdkName] = data;
      } catch (error) {
        exportData.errors.push({ sdk: sdkName, error: error.message });
      }
    }

    // 添加同意记录
    exportData.consentHistory = consentManager.getConsentLog?.() || 'Not available';

    return exportData;
  }

  /**
   * 获取全平台数据使用声明
   * 汇总所有 SDK 的数据收集声明
   * 
   * @returns {object} 统一的数据声明
   */
  getFullDataDisclosure() {
    const disclosure = {
      platform: 'QuantaBit',
      lastUpdated: new Date().toISOString(),
      sdks: {},
      consentLevels: {
        essential: '核心安全功能（身份验证、防欺诈），无需同意。',
        functional: '增强体验（推送通知、钱包功能），需要用户同意。',
        analytics: '使用分析和行为追踪，需要用户明确同意。',
        marketing: '营销和广告定向，需要用户明确同意。',
      },
    };

    for (const [sdkName, disclosureFn] of this._disclosures) {
      try {
        disclosure.sdks[sdkName] = disclosureFn();
      } catch (e) {
        disclosure.sdks[sdkName] = { error: e.message };
      }
    }

    return disclosure;
  }

  /**
   * 撤销所有同意并停止所有追踪
   */
  revokeAllConsent() {
    consentManager.init();
    consentManager.acceptEssentialOnly();
    return { success: true, timestamp: new Date().toISOString() };
  }

  /**
   * 获取已注册的 SDK 数量
   */
  get registeredSDKs() {
    return {
      cleaners: [...this._cleaners.keys()],
      exporters: [...this._exporters.keys()],
      disclosures: [...this._disclosures.keys()],
    };
  }

  /**
   * 清除通用 localStorage 数据
   * @private
   */
  _clearGenericLocalStorage() {
    if (typeof localStorage === 'undefined') return;
    
    const prefixes = [
      'qbit_', 'im_', 'tracking_', 'analytics_', 
      'wallet_', 'push_', 'notification_',
    ];
    
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && prefixes.some(prefix => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  // ============ 自动注册辅助 ============

  /**
   * 从 SDK 实例自动注册隐私处理器
   * SDK 只需实现 clearAllUserData/exportXxxData/getDataDisclosure 任一方法即可
   * 
   * @param {string} sdkName - SDK 名称
   * @param {object} instance - SDK 实例
   * 
   * @example
   * // 在 SDK 初始化时自动注册
   * privacyOrchestrator.registerFromInstance('search-sdk', searchApi);
   */
  registerFromInstance(sdkName, instance) {
    const handlers = {};
    
    // 自动检测清理方法
    const cleanMethods = ['clearAllUserData', 'clearData', 'clearLocal', 'gdprClear'];
    for (const method of cleanMethods) {
      if (typeof instance[method] === 'function') {
        handlers.clean = () => instance[method]();
        break;
      }
    }

    // 自动检测导出方法
    const exportMethods = [
      'exportMyData', 'exportSearchData', 'exportPushData',
      'exportPaymentData', 'exportKycData', 'exportChatData',
      'exportData',
    ];
    for (const method of exportMethods) {
      if (typeof instance[method] === 'function') {
        handlers.export = (opts) => instance[method](opts);
        break;
      }
    }

    // 自动检测声明方法 
    if (typeof instance.getDataDisclosure === 'function') {
      handlers.getDisclosure = () => instance.getDataDisclosure();
    }

    if (Object.keys(handlers).length > 0) {
      this.register(sdkName, handlers);
    }
  }

  /**
   * 批量注册多个 SDK
   * @param {Object<string, object>} sdkMap - { sdkName: instance }
   */
  registerBatch(sdkMap) {
    for (const [name, instance] of Object.entries(sdkMap)) {
      this.registerFromInstance(name, instance);
    }
  }
}

export const privacyOrchestrator = new PrivacyOrchestratorClass();

export { PrivacyOrchestratorClass };

export default privacyOrchestrator;
